import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Heading } from "react-aria-components";
import z from "zod";

import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form";
import { Link } from "@/components/base/link";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import { getTournamentDivisionDisplay } from "@/hooks/tournament";
import { useTRPC } from "@/integrations/trpc/react";

const updateDivisionPriceSchema = z.object({
  id: z.number(),
  registrationPrice: z.number().nullable(),
});

const updateDivisionPricesSchema = z.object({
  divisions: z.array(updateDivisionPriceSchema).min(1),
});

export type ManagePricesFormProps = {
  tournamentId: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ManagePricesForm({
  tournamentId,
  onOpenChange,
  ...props
}: ManagePricesFormProps) {
  const trpc = useTRPC();
  const tournamentQueryOpts = trpc.tournaments.get.queryOptions({
    id: tournamentId,
  });

  const { data: tournament } = useQuery({
    ...tournamentQueryOpts,
    enabled: props.isOpen,
  });

  const { data: registrationSettings } = useQuery(
    trpc.settings.getRegistrationSettings.queryOptions(),
  );

  const queryClient = useQueryClient();

  const { mutate, failureReason } = useMutation({
    ...trpc.tournaments.updateDivisionPrices.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.tournaments.pathFilter());

      onOpenChange(false);
    },
  });

  const schema = updateDivisionPricesSchema;

  const form = useAppForm({
    defaultValues: {
      divisions:
        tournament?.tournamentDivisions.map(({ id, registrationPrice }) => ({
          id,
          registrationPrice: registrationPrice ?? null,
        })) ?? [],
    } as z.infer<typeof schema>,
    validators: {
      onMount: schema,
      onChange: schema,
    },
    onSubmit: ({ value: { divisions }, formApi }) => {
      const hasChange = divisions.some((d, i) => {
        const original =
          tournament?.tournamentDivisions.find((td) => td.id === d.id)
            ?.registrationPrice ?? null;
        return d.registrationPrice !== original;
      });

      if (!hasChange) {
        onOpenChange(false);
        return;
      }

      mutate(
        { divisions },
        {
          onSuccess: () => {
            formApi.reset();
          },
        },
      );
    },
  });

  return (
    <Modal {...props} onOpenChange={onOpenChange}>
      <div className="relative flex flex-col space-y-4 p-3">
        <Heading className={title({ size: "sm" })} slot="title">
          Registration Prices
        </Heading>

        <p className="text-sm text-gray-700">
          Set the registration price for all divisions at once. Leave blank to
          use the default price. To edit other division settings, use{" "}
          <Link
            to="/tournaments/$tournamentId/edit"
            params={{ tournamentId: String(tournamentId) }}
            className="text-primary underline"
            onPress={() => onOpenChange(false)}
          >
            Edit Tournament
          </Link>
          .
        </p>

        {tournament ? (
          <form
            className="flex flex-col space-y-2"
            onSubmit={(e) => {
              e.preventDefault();

              form.handleSubmit();
            }}
          >
            {failureReason && (
              <form.AppForm>
                <form.Alert
                  title="Unable to update prices"
                  description={failureReason.message}
                />
              </form.AppForm>
            )}

            <div className="grid grid-cols-6 gap-2">
              <span className="col-span-3 text-sm font-medium text-gray-700">
                Division
              </span>
              <span className="col-span-3 text-sm font-medium text-gray-700">
                Price
              </span>
            </div>

            <form.AppField name="divisions" mode="array">
              {(field) =>
                field.state.value?.map(({ id }, i) => {
                  const division = tournament.tournamentDivisions.find(
                    (d) => d.id === id,
                  );

                  if (!division) return null;

                  const defaultPrice =
                    division.division.name === "open"
                      ? registrationSettings?.defaultOpenPrice
                      : registrationSettings?.defaultPrice;

                  return (
                    <div
                      key={id}
                      className="grid grid-cols-6 items-center gap-2"
                    >
                      <div className="col-span-3 text-sm">
                        {getTournamentDivisionDisplay(division)}
                      </div>
                      <form.AppField name={`divisions[${i}].registrationPrice`}>
                        {(subField) => (
                          <subField.Number
                            field={subField}
                            className="col-span-3"
                            placeholder={
                              defaultPrice != null
                                ? `Default: $${defaultPrice}`
                                : undefined
                            }
                          />
                        )}
                      </form.AppField>
                    </div>
                  );
                })
              }
            </form.AppField>

            <form.AppForm>
              <form.Footer>
                <Button onPress={() => onOpenChange(false)}>Cancel</Button>

                <form.SubmitButton requireChange={false}>
                  Save
                </form.SubmitButton>
              </form.Footer>
            </form.AppForm>
          </form>
        ) : (
          <p className="text-sm text-gray-500">Loading...</p>
        )}
      </div>
    </Modal>
  );
}
